/**
 * VideoSection Component
 * Main video section with drag-and-drop support and upload orchestration
 */

import { useRef, useState } from 'react';
import { useProspectVideos } from '../../../../../hooks/useProspectVideos';
import { useVideoUpload } from '../../../../../hooks/useVideoUpload';
import { useVideoFilterOptions } from '../../../../../hooks/useVideoFilterOptions';
import { useVideosContext } from '../../../../../contexts/VideosContext';
import {
  EMPTY_VIDEO_FILTERS,
  hasActiveVideoFilters,
  type VideoFilters,
} from '../../../../../types/prospect-media';
import { validateVideoFile } from '../../../../../utils/video-validation';
import { ConfirmModal } from '../../../../../components/ConfirmModal';
import { Button } from '../../../../../components/Button';
import { NotifyStaffModal } from '../../../../../components/NotifyStaffModal';
import { VideoFilterBar } from '../../../../../components/VideoFilterBar';
import { VideoUploadButton } from './VideoUploadButton';
import { VideoUploadProgress } from './VideoUploadProgress';
import { VideoGrid } from './VideoGrid';
import { VideoPlayer } from './VideoPlayer';
import { VideoClipEditor } from './VideoClipEditor';
import './VideoSection.css';

interface VideoSectionProps {
  prospectId: string;
}

export function VideoSection({ prospectId }: VideoSectionProps) {
  const [filters, setFilters] = useState<VideoFilters>(EMPTY_VIDEO_FILTERS);
  const isFiltered = hasActiveVideoFilters(filters);

  const {
    videos,
    loading,
    error,
    refresh,
    deleteVideo,
    getVideoUrl,
    getThumbnailUrl,
    needsRefresh,
  } = useProspectVideos(prospectId, filters);

  const { options, loading: optionsLoading } = useVideoFilterOptions();
  const { uploadVideo, isUploading, progress } = useVideoUpload();
  const orgVideosContext = useVideosContext();

  // Latches once the gallery has ever had something in it. Deriving this from the current
  // list instead would unmount the bar — and the focused <input> inside it — the moment a
  // search is cleared from a zero-result state: `isFiltered` and `videos.length` both go
  // falsy in the same commit, focus lands on <body>, and the next keystrokes go nowhere.
  const hasEverHadVideos = useRef(false);
  if (videos.length > 0) hasEverHadVideos.current = true;
  const filteringVisible = hasEverHadVideos.current || isFiltered;

  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
  const [clippingVideoId, setClippingVideoId] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  // The video just uploaded, offered for a staff tag until the coach acts on it or dismisses it.
  const [justUploadedId, setJustUploadedId] = useState<string | null>(null);
  const [notifyVideoId, setNotifyVideoId] = useState<string | null>(null);

  const selectedVideo = videos.find((v) => v.id === selectedVideoId);
  const clippingVideo = videos.find((v) => v.id === clippingVideoId);

  // Handle file upload (from button or drag-and-drop)
  const handleUpload = async (file: File) => {
    setUploadError(null);
    const result = await uploadVideo(prospectId, file);

    if (result.success) {
      // Right after an upload is when a coach knows who needs to see it.
      if (result.mediaId) setJustUploadedId(result.mediaId);

      // Refresh video list after successful upload
      try {
        // Refresh prospect-specific videos
        await refresh();

        // Refresh org-wide videos
        await orgVideosContext.refresh();
      } catch (err) {
        console.error('[VideoSection] Failed to refresh after upload', {
          error: err,
          prospectId,
          mediaId: result.mediaId,
        });
        setUploadError('Video uploaded successfully, but failed to refresh the list. Please refresh the page.');
      }
    } else {
      console.error('[VideoSection] Upload failed', {
        error: result.error,
        prospectId,
        fileName: file.name,
        uploadTimeMs: result.uploadTimeMs,
      });
      setUploadError(result.error || 'Upload failed. Please try again.');
    }
  };

  // Handle video thumbnail click
  const handleVideoClick = (videoId: string) => {
    setSelectedVideoId(videoId);
  };

  // Handle video player close
  const handleClosePlayer = () => {
    setSelectedVideoId(null);
  };

  // Handle video delete request (show confirmation modal)
  const handleDeleteClick = (videoId: string) => {
    setVideoToDelete(videoId);
  };

  // Open the clip editor for a source video
  const handleClipClick = (videoId: string) => {
    setClippingVideoId(videoId);
  };

  // New clips are ordinary videos, so both galleries need to pick them up.
  const handleClipsCreated = async () => {
    try {
      await refresh();
      await orgVideosContext.refresh();
    } catch (err) {
      console.error('[VideoSection] Failed to refresh after clipping', {
        error: err,
        prospectId,
      });
      setUploadError('Clips saved, but the list failed to refresh. Please refresh the page.');
    }
  };

  // Handle confirmed video deletion
  const handleConfirmDelete = async () => {
    if (!videoToDelete) return;

    setDeleteError(null);
    try {
      await deleteVideo(videoToDelete);
      setVideoToDelete(null);

      // Refresh org-wide videos after deletion
      try {
        await orgVideosContext.refresh();
      } catch (err) {
        console.error('[VideoSection] Failed to refresh org videos after delete', {
          error: err,
          prospectId,
          videoId: videoToDelete,
        });
      }
    } catch (err) {
      setDeleteError(`Failed to delete video: ${err instanceof Error ? err.message : String(err)}`);
      setVideoToDelete(null);
    }
  };

  // Drag-and-drop event handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set isDragging false if leaving the video-section entirely
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (isUploading || isValidating) {
      console.log('[VideoSection] Drop ignored - upload or validation in progress');
      return;
    }

    const files = Array.from(e.dataTransfer.files);
    const videoFile = files.find((file) => file.type.startsWith('video/'));

    if (!videoFile) {
      setUploadError('Please drop a video file (MP4, MOV, or AVI)');
      return;
    }

    setIsValidating(true);
    try {
      // Validate file
      const validation = await validateVideoFile(videoFile);
      if (!validation.valid) {
        setUploadError(validation.error || 'Invalid video file');
        return;
      }

      handleUpload(videoFile);
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <div
      className={`video-section ${isDragging ? 'video-section--dragging' : ''}`}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <div className="video-section__header">
        <h3 className="video-section__title">Video Gallery</h3>
        <VideoUploadButton
          onUpload={handleUpload}
          onValidationError={setUploadError}
          disabled={isUploading || isValidating}
        />
      </div>

      {/* Hidden until the gallery has had something in it — a filter bar over an empty
          gallery is a control with nothing to act on. */}
      {filteringVisible && (
        <VideoFilterBar
          filters={filters}
          onChange={setFilters}
          onClear={() => setFilters(EMPTY_VIDEO_FILTERS)}
          options={options}
          optionsLoading={optionsLoading}
          // This endpoint is unpaginated, so the rendered list is the whole match set.
          resultCount={videos.length}
          compact
        />
      )}

      {needsRefresh && (
        <div className="video-section__refresh-banner">Refreshing video URLs...</div>
      )}

      {loading && !videos.length && (
        <div className="video-section__loading">Loading videos...</div>
      )}

      {error && (
        <div className="video-section__error">
          Failed to load videos: {error.message}
        </div>
      )}

      {uploadError && (
        <div className="video-section__error">
          {uploadError}
          <button
            className="video-section__error-dismiss"
            onClick={() => setUploadError(null)}
            aria-label="Dismiss error"
          >
            ×
          </button>
        </div>
      )}

      {justUploadedId && (
        <div className="video-section__uploaded" role="status">
          <span className="video-section__uploaded-text">Video uploaded.</span>
          <div className="video-section__uploaded-actions">
            <Button
              variant="primary"
              onClick={() => {
                setNotifyVideoId(justUploadedId);
                setJustUploadedId(null);
              }}
            >
              Notify a coach
            </Button>
            <Button variant="secondary" onClick={() => setJustUploadedId(null)}>
              Not now
            </Button>
          </div>
        </div>
      )}

      {deleteError && (
        <div className="video-section__error">
          {deleteError}
          <button
            className="video-section__error-dismiss"
            onClick={() => setDeleteError(null)}
            aria-label="Dismiss error"
          >
            ×
          </button>
        </div>
      )}

      {!loading && !error && videos.length === 0 && (
        <div className="video-section__empty">
          {isFiltered ? (
            <>
              <p>No videos match these filters</p>
              <p className="video-section__empty-hint">
                <button
                  type="button"
                  className="video-section__empty-clear"
                  onClick={() => setFilters(EMPTY_VIDEO_FILTERS)}
                >
                  Clear filters
                </button>{' '}
                to see the whole gallery
              </p>
            </>
          ) : (
            <>
              <p>No videos yet</p>
              <p className="video-section__empty-hint">
                Click "Upload Video" or drag and drop a video file here
              </p>
            </>
          )}
        </div>
      )}

      {videos.length > 0 && (
        <VideoGrid
          videos={videos}
          onVideoClick={handleVideoClick}
          onDeleteClick={handleDeleteClick}
          getThumbnailUrl={getThumbnailUrl}
          onClipClick={handleClipClick}
        />
      )}

      {/* Drag-and-drop overlay */}
      {isDragging && !isUploading && !isValidating && (
        <div className="video-section__drop-overlay">
          <div className="video-section__drop-content">
            <svg
              className="video-section__drop-icon"
              width="64"
              height="64"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
            </svg>
            <p className="video-section__drop-text">Drop video file to upload</p>
          </div>
        </div>
      )}

      {isUploading && <VideoUploadProgress progress={progress} />}

      {selectedVideoId && (
        <VideoPlayer
          videoId={selectedVideoId}
          prospectId={prospectId}
          getVideoUrl={getVideoUrl}
          onClose={handleClosePlayer}
          clipStartSeconds={selectedVideo?.clipStartSeconds}
          clipEndSeconds={selectedVideo?.clipEndSeconds}
          onNotify={() => {
            // Close the player first so the video is not still playing behind the dialog.
            setNotifyVideoId(selectedVideoId);
            setSelectedVideoId(null);
          }}
        />
      )}

      <NotifyStaffModal
        isOpen={notifyVideoId !== null}
        prospectId={prospectId}
        itemType="video"
        itemId={notifyVideoId ?? ''}
        onClose={() => setNotifyVideoId(null)}
      />

      {clippingVideoId && (
        <VideoClipEditor
          prospectId={prospectId}
          videoId={clippingVideoId}
          fallbackDurationSeconds={clippingVideo?.durationSeconds}
          getVideoUrl={getVideoUrl}
          onClose={() => setClippingVideoId(null)}
          onClipsCreated={handleClipsCreated}
        />
      )}

      <ConfirmModal
        isOpen={videoToDelete !== null}
        onClose={() => setVideoToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Video"
        message="Are you sure you want to delete this video? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
}
